import {useState, FC} from 'react';
import {useSelector} from 'react-redux';

import HappyTimerClockIcon from '@stencil/icons/src/HappyTimerClockIcon/HappyTimerIcon';
import {defaultTheme} from '@stencil/build-theme';
import Timer from '@stencil/countdowntimer';

import * as S from './noworneverdealtimercg.styles';
import {srpSrpConfigDataSelector} from '@stencil/hotels-cg-reducer/src/srp/selectors';

interface PersuasionProps {
  ic?: string;
  persuasionText?: string;
  colourCodeData?: {
    textColour: string;
    bgColour: string;
    borderColour: string;
    gradientStartColour: string;
    gradientEndColour: string;
  };
  templateType?: number;
}

interface NoworNeverDealProps {
  isRowAligned?: boolean;
  persuasiondata: [PersuasionProps];
  customStyle?: object;
  hideTimer?: boolean;
}

const NoworNeverDealSRPCg: FC<NoworNeverDealProps> = ({
  isRowAligned = false,
  persuasiondata = [],
  customStyle = {},
  hideTimer = false,
}) => {
  const [isTimeExpired, setIsTimeExpired] = useState(false);
  const {isSeo} = useSelector(srpSrpConfigDataSelector);

  const firstPers = persuasiondata[0];
  const isDSD = firstPers?.templateType === 9;
  const isSupplierDeal = firstPers?.templateType === 16;

  if (!(persuasiondata && persuasiondata.length) || (!isDSD && !isSupplierDeal)) {
    return null;
  }

  const getDealIcon = (item, isHeading = false) => {
    return isDSD ? (
      <HappyTimerClockIcon
        height="1.2rem"
        width="1.2rem"
        margin="0 0.5rem 0 0"
        fill={defaultTheme.colors.goWhite.base}
      />
    ) : (
      <S.LMD_Image src={item?.imageUrl} isHeading={isHeading} />
    );
  };

  const renderRight = (item, index, bigFont = false) => {
    if (hideTimer && (index === 1 || index === 2)) {
      return null;
    }

    return (
      <S.DealText tColor={item?.colourCodeData?.textColour} bigFont={bigFont}>
        {index === 2 ? (
          <>
            <Timer
              timeInMs={item?.persuasionText}
              timerTextFromServer
              setIsTimeExpired={setIsTimeExpired}
              isSeo={isSeo}
            />
            {!isDSD && item?.isParentheses && !isTimeExpired ? ')' : null}
          </>
        ) : (
          !isTimeExpired &&
          !isSeo && (
            <>
              {index === 1 && !isDSD && item?.isParentheses ? '(' : null}
              <span dangerouslySetInnerHTML={{__html: item?.persuasionText}} />
            </>
          )
        )}
      </S.DealText>
    );
  };

  const middleIndex =
    persuasiondata?.filter(item => item?.templateType == firstPers?.templateType)?.length === 4
      ? 3
      : 1; // 4 length is on review page for last min deal.

  return !isRowAligned ? (
    <S.NoworNeverPersuationWrapper isDSD={isDSD}>
      <S.DealPara
        isDSD={isDSD}
        bgColor={firstPers?.[0]?.colourCodeData?.bgColour}
        bdcColor={firstPers?.[0]?.colourCodeData?.borderColour}
        gs={firstPers?.[0]?.colourCodeData?.gradientStartColour}
        ge={firstPers?.[0]?.colourCodeData?.gradientEndColour}
        isHeading
      >
        {getDealIcon(firstPers, true)}
        <S.DealText tColor={firstPers?.[0]?.colourCodeData?.textColour}>
          {firstPers?.[0]?.persuasionText}
        </S.DealText>
      </S.DealPara>
      <S.FlexContainerWrap>
        {persuasiondata?.data?.map((item, index) => {
          if (index > 0)
            return (
              <S.DealPara bgColor={item?.colourCodeData?.bgColour} isDSD={isDSD}>
                {renderRight(item, index)}
              </S.DealPara>
            );
          return null;
        })}
      </S.FlexContainerWrap>
    </S.NoworNeverPersuationWrapper>
  ) : (
    <S.NoworNeverDetailReviewWrapper
      isDSD={isDSD}
      gs={firstPers?.[0]?.colourCodeData?.gradientStartColour}
      ge={firstPers?.[0]?.colourCodeData?.gradientEndColour}
      customStyle={customStyle}
    >
      {persuasiondata?.[0]?.data?.map((item, index) => {
        if (index === 0) {
          return (
            <S.NoworNeverTag bgColor={isDSD ? item?.colourCodeData?.bgColour : ''} isHeading>
              <>
                {getDealIcon(item)}
                <S.DealText tColor={item?.colourCodeData?.textColour} bigFont={!isDSD}>
                  {item?.persuasionText}
                </S.DealText>
              </>
            </S.NoworNeverTag>
          );
        }
        return (
          <S.NoworNeverTag middleSpan={middleIndex === index}>
            {renderRight(item, index, !isDSD)}
          </S.NoworNeverTag>
        );
      })}
    </S.NoworNeverDetailReviewWrapper>
  );
};

export default NoworNeverDealSRPCg;
